/* eslint-disable jsdoc/require-jsdoc */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { ReactElement, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import Button from '../controls/Button';
import SidePanelContainer from '../controls/SidePanelContainer';
import { TOAST_MESSAGES } from '../constants/global-constants';
import { createEvent } from '../services/dashboard-service';
import { useData } from '../context/data.context';
import MultiSelectInputBox from '../components/MultiSelectInputBox';
import CustomTextArea from '../controls/CustomTextArea';
import CustomDatePicker from '../controls/CustomDatePicker';
import MultiSelectDropdown from '../controls/MultiSelectDropDown';

type FORMDATA = {
  comments: string;
  eventNotes: string;
  startTime: Date;
  endTime: Date;
  devices: { label: string; value: string }[];
  groups: { label: string; value: string }[];
};

type AddNotesModalProps = {
  handleClose: () => void;
};

/**
 * Modal - AddNotes.
 * @param props - Properties of AddNotesModal.
 * @returns ReactElement.
 */
export default function AddEventsModal(props: AddNotesModalProps): ReactElement {
  const { handleClose } = props;
  const { fetchEvents, devices, groupedData: groups } = useData();
  const tagInputRef = useRef<any>(null);
  const [reasonChips, setReasonChips] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const methods = useForm<FORMDATA>({
    mode: 'all',
    defaultValues: {
      startTime: new Date(),
      endTime: new Date()
    }
  });
  const { handleSubmit, control } = methods;

  /**
   * Handles API call for activities put API.
   * @param data - Formdata.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleFormSubmit = async (data: FORMDATA): Promise<void> => {
    const allChips = tagInputRef.current.forceAddChip();
    try {
      setLoading(true);
      const devicesList: string[] = [];
      const selectedGroups =
        data.groups?.map((group) => groups.find((item) => item.groupId === Number(group.value))) ||
        [];
      selectedGroups.forEach((group) => {
        if (group?.sensorDetailsList) {
          devicesList.push(...group.sensorDetailsList.map((item) => String(item.serial_number)));
        }
      });
      data.devices?.forEach((device) => devicesList.push(device.value));
      await createEvent({
        id: 0,
        eventNotes: data.eventNotes,
        eventTime: new Date(data.startTime || '').getTime(),
        tags: allChips,
        comments: data.comments,
        eventType: 'EVENTS',
        devices: devicesList,
        groups: []
      });
      toast.success(TOAST_MESSAGES.added);
      await fetchEvents();
      setLoading(false);
      handleClose();
    } catch (e) {
      setLoading(false);
      toast.success(TOAST_MESSAGES.error);
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  return (
    <SidePanelContainer closeModal={() => handleClose()}>
      <div className="pt-3 flex text-gray-500 dark:text-gray-300 text-lg">Add Event</div>
      <div className="pb-3 flex text-gray-500 dark:text-gray-500 text-sm">
        Create events based on time
      </div>
      {/* eslint-disable-next-line */}
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(handleFormSubmit)}>
          <CustomTextArea
            height={80}
            label="Event"
            validationObject={{ required: 'Please enter the event.' }}
            idname="eventNotes"
          />
          <CustomDatePicker
            label="Start Time"
            id="startTime"
            validationObject={{ required: 'Please enter the start time' }}
            control={control}
            defaultValue={methods.getValues('startTime')}
          />
          <CustomDatePicker
            label="End Time"
            id="endTime"
            validationObject={{ required: 'Please enter the end time' }}
            control={control}
            defaultValue={methods.getValues('endTime')}
          />

          <CustomTextArea height={80} label="Comments" idname="comments" />
          <MultiSelectDropdown
            id="devices"
            label="Devices"
            control={control}
            options={devices.map((device) => ({
              label: device.deviceName.toString(),
              value: device.serialNumber.toString()
            }))}
          />
          <MultiSelectDropdown
            id="groups"
            label="Groups"
            control={control}
            options={groups.map((group) => ({
              label: group.groupName,
              value: group.groupId.toString()
            }))}
          />
          <MultiSelectInputBox
            label="Tags"
            availableOptions={[]}
            ref={tagInputRef}
            chips={reasonChips}
            height={160}
            setChips={setReasonChips}
            acceptsSentence={false}
          />
          <div
            style={{ width: 'inherit' }}
            className="p-5 fixed bottom-0 right-0 flex justify-center gap-3 bg-white dark:bg-container-600"
          >
            <div>
              <Button
                theme="secondary-color"
                size="sm"
                name="Discard"
                onClick={() => handleClose()}
                isBlockButton
              />
            </div>
            <Button
              theme="primary"
              size="sm"
              name="Save Changes"
              buttonType="submit"
              loading={loading}
              isBlockButton
            />
          </div>
        </form>
      </FormProvider>
    </SidePanelContainer>
  );
}
